import { formatTanggal } from "@/utils/dateFormate";
import { formatCurrency } from "@/utils/formatCurrency";
import { useActivityDetail } from "../hooks/useActivity";
import { getStoredUserData } from "@/utils/getStoredUser";
import Error from "@/components/fragments/Error";
import Loading from "@/components/fragments/Loadin";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";

import toast from "react-hot-toast";
import { useTransactionStore } from "@/features/Cart/store/transactionStore";
import { useShallow } from "zustand/shallow";

export default function ActivityDetailBase({
  outNo,
}: {
  outNo: string | undefined;
}) {
  const [showshowModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [replaceTransaction] = useTransactionStore(
    useShallow((state) => [state.replaceTransaction]),
  );
  const userData = getStoredUserData();
  let email = userData?.email || "";

  const {
    data: transactions,
    isLoading,
    isError,
    error,
  } = useActivityDetail({ email, outNo });

  if (isLoading) {
    return (
      <Loading fullScreen={true} message="Loading transaction details..." />
    );
  }

  if (isError) {
    // Extract status code dari error React Query
    const statusCode = (error as any)?.response?.status;
    return <Error statusCode={statusCode} fullScreen={true} />;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Selesai":
        return "bg-[#dcfce7] text-[#166534]";
      case "Pending":
        return "bg-[#fef9c3] text-[#854d0e]";
      default:
        return "bg-[#f3f4f6] text-[#1f2937]";
    }
  };

  const mapTransactionToCartFormat = (transactions: any) => {
    return {
      cart: transactions.details.map((item: any) => ({
        mtrl_code: item.mtrl_code,
        mtrl_name: item.mtrl_name,
        cat_name: "", // tidak ada di source, jadi default ""
        sell_price: item.sell_price,
        stock: item.stock_available, // tidak ada di source, isi sesuai kebutuhan
        satuan: item.satuan,
        qty: item.qty,
        discount: item.discount || 0, // rupiah
        subtotal: item.subtotal,
      })),
      out_no: transactions.out_no,
      tanggalTransaksi: transactions.out_date,
      metodePembayaran: "Cash", // tidak ada di source
      customer: transactions.cust_id,
      customerName: transactions.cust_name,
      diskonSubtotal: transactions.discount_total || 0, // rupiah
      ppn: transactions.ppn || 0, // rupiah
      paidAmount: transactions.totalbayar,
      changeAmount: transactions.kembalian,
    };
  };

  const handleLanjutkanTransaksi = () => {
    const mappedData = mapTransactionToCartFormat(transactions);

    try {
      replaceTransaction(mappedData);
      localStorage.setItem("transaction", JSON.stringify(mappedData));
      toast.success("okee, silahkan lanjutkan transaksi.");

      navigate("/catalog", { replace: true });
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }
  };

  return (
    <div className="min-h-screen p-2 mb-10">
      <div className="">
        {/* Main Card */}
        <div className="overflow-hidden bg-white dark:bg-transparent">
          {/* Header Card */}
          <div className="p-4 text-white">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg font-regular text-[#1e1e1e] dark:text-white">
                    No. Transaksi:
                  </span>
                  <span className=" font-regular text-[#1e1e1e] dark:text-white">
                    {transactions?.out_no}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#1e1e1e]/90 dark:text-white">
                    {formatTanggal(String(transactions?.out_date))}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#ffecba] text-[#92400e]">
                    {transactions?.cust_name || "Umum"}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      transactions?.status,
                    )}`}
                  >
                    {transactions?.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Detail Produk */}
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4 text-[#1e1e1e] dark:text-white">
              Detail Produk
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#efecff]">
                    <th className="text-left p-2 font-medium text-[#1e1e1e] dark:text-white">
                      Produk
                    </th>
                    <th className="text-right p-2 font-medium text-[#1e1e1e] dark:text-white">
                      Harga
                    </th>
                    <th className="text-right p-2 font-medium text-[#1e1e1e] dark:text-white">
                      Diskon
                    </th>
                    <th className="text-right p-2 font-medium text-[#1e1e1e] dark:text-white">
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions &&
                    transactions.details.map((item, index) => (
                      <tr
                        key={index}
                        className="border-b border-[#efecff]  hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <td className="p-2">
                          <div>
                            <div className="font-regular text-xs lg:text-sm text-[#1e1e1e] dark:text-white">
                              {item.mtrl_name}
                            </div>
                            <div className="text-xs lg:text-sm text-[#1e1e1e]/90 dark:text-white mt-1">
                              {item.qty} / {item.satuan}
                            </div>
                          </div>
                        </td>
                        <td className="text-right py-4 px-4 font-light text-xs lg:text-sm text-[#1e1e1e] dark:text-white">
                          {formatCurrency(String(item.sell_price))}
                        </td>
                        <td className="text-right py-4 px-4 font-light text-xs lg:text-sm text-[#ef4444] dark:text-red-300">
                          {formatCurrency(String(item.discount))}
                        </td>
                        <td className="text-right py-4 px-4 font-light text-xs lg:text-sm text-[#1e1e1e] dark:text-white">
                          {formatCurrency(String(item.subtotal))}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ringkasan Pembayaran */}
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4 text-[#1e1e1e] dark:text-white">
              Ringkasan Pembayaran
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Ringkasan */}
              <div className="space-y-3">
                <div className="flex justify-between text-[#1e1e1e] dark:text-white">
                  <span className="text-sm dark:text-white">Total Harga</span>
                  <span className="text-sm font-light dark:text-white">
                    {formatCurrency(String(transactions?.total_price))}
                  </span>
                </div>
                <div className="flex justify-between text-[#1e1e1e] dark:text-white">
                  <span className="text-sm dark:text-white">Diskon Total</span>
                  <span className="font-light text-sm text-[#ff7075]">
                    -{formatCurrency(String(transactions?.discount_total))}
                  </span>
                </div>
                <div className="flex justify-between text-[#1e1e1e] dark:text-white">
                  <span className="text-sm dark:text-white">PPN</span>
                  <span className="text-sm font-light dark:text-white">
                    {formatCurrency(String(transactions?.ppn))}
                  </span>
                </div>
                <div className="flex justify-between pt-3 border-t border-[#efecff]">
                  <span className="font-medium text-[#1e1e1e] dark:text-white">
                    Total Bayar
                  </span>
                  <span className="font-medium  text-[#1e1e1e] dark:text-white">
                    {formatCurrency(String(transactions?.totalbayar))}
                  </span>
                </div>
                <div className="flex justify-between border-[#efecff]">
                  <span className="font-medium text-[#1e1e1e] dark:text-white">
                    Grand Total
                  </span>
                  <span className="font-medium  text-[#1e1e1e] dark:text-white">
                    {formatCurrency(String(transactions?.grand_total))}
                  </span>
                </div>
                <div className="flex justify-between pt-3 border-t border-[#efecff]">
                  <span className="font-medium text-[#1e1e1e] dark:text-white">
                    Kembalian
                  </span>
                  {transactions?.is_pending == "1" && (
                    <span className="font-medium px-3 py-1 rounded-lg  text-[#1e1e1e] bg-amber-200">
                      pending
                    </span>
                  )}
                  {transactions?.is_pending != "1" && (
                    <span className="font-medium  text-[#1e1e1e] dark:text-white">
                      {formatCurrency(String(transactions?.kembalian))}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 mt-8">
          {transactions && transactions?.is_pending == "1" && (
            <button
              onClick={() => setShowModal(true)}
              className="cursor-pointer group flex items-center gap-2 px-6 py-3 rounded-lg font-medium 
            text-white bg-[#37393d] 
               hover:bg-[#2c2e33] hover:shadow-md 
               transition-all duration-200 active:scale-[0.98]"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="#fff"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Selesaikan Transaksi
            </button>
          )}

          <Link to={`/struk/${transactions?.out_no}`}>
            <button
              className="group flex items-center gap-2 px-6 py-3 rounded-lg font-medium 
               border border-[#d7d0fe] text-[#1e1e1e] dark:text-white 
               hover:bg-[#f8f5ff] hover:border-[#c4b5fd] 
               transition-all duration-200 active:scale-[0.98] dark:text-white dark:hover:text-black"
            >
              <svg
                className="w-5 h-5 text-[#1e1e1e] dark:text-white group-hover:text-[#4f46e5]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Cetak Struk
            </button>
          </Link>
        </div>

        {showshowModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowModal(false)}
            />
            <div className="relative bg-white dark:bg-[#37393d] rounded-2xl p-6 w-[90%] max-w-sm shadow-2xl z-10 animate-in fade-in zoom-in duration-200">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Selesaikan Transaksi ?
              </h2>
              <p className="mt-3 text-sm text-gray-600 dark:text-white">
                Apakah Anda yakin ingin selesaikan transaksi ini ?
              </p>
              <div className="flex gap-3 mt-6 ">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowModal(false)}
                >
                  Tidak
                </Button>
                <Button
                  className="flex-1 font-semibold text-white bg-green-500"
                  onClick={handleLanjutkanTransaksi}
                >
                  Ya, Selesaikan
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
